import { Spin, type SpinProps, theme } from 'antd';
import React, {
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import Empty from '@/components/QuietEmpty';
import styles from './style.module.less';

export interface CrmListProps<T> {
  dataSource?: T[];
  renderItem?: (item: T, index: number) => ReactNode;
  rowKey?: keyof T | ((item: T) => React.Key);
  size?: 'small' | 'default' | 'large';
  split?: boolean;
  loading?: boolean | SpinProps;
  locale?: { emptyText: ReactNode };
  header?: ReactNode;
  footer?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

/** Static CRM lists; rows keep their natural height and use the current theme. */
function CrmListRoot<T>({
  dataSource = [],
  renderItem,
  rowKey,
  size = 'default',
  split = true,
  loading = false,
  locale,
  header,
  footer,
  className,
  style,
}: CrmListProps<T>) {
  const { token } = theme.useToken();
  const spinning =
    typeof loading === 'boolean' ? loading : loading.spinning !== false;
  return (
    <div
      className={[
        styles.list,
        size === 'small' ? styles.small : size === 'large' ? styles.large : '',
        split ? styles.split : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={
        {
          '--crm-list-color': token.colorText,
          '--crm-list-secondary': token.colorTextDescription,
          '--crm-list-border': token.colorSplit,
          ...style,
        } as CSSProperties
      }
      aria-busy={spinning}
    >
      {header != null && <div className={styles.header}>{header}</div>}
      <Spin
        {...(typeof loading === 'object' ? loading : {})}
        spinning={spinning}
      >
        {dataSource.length ? (
          <ul className="crm-list-items">
            {dataSource.map((item, index) => {
              const rendered = renderItem?.(item, index);
              const record = item as {
                uuid?: React.Key;
              } | null;
              const key =
                typeof rowKey === 'function'
                  ? rowKey(item)
                  : rowKey != null
                    ? (item[rowKey] as React.Key)
                    : React.isValidElement(rendered) && rendered.key != null
                      ? rendered.key
                      : (record?.uuid ?? index);
              return <li key={key}>{rendered}</li>;
            })}
          </ul>
        ) : !spinning ? (
          <div className={styles.empty}>
            {locale?.emptyText ?? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </div>
        ) : null}
      </Spin>
      {footer != null && <div className={styles.footer}>{footer}</div>}
    </div>
  );
}

function Item({
  actions,
  extra,
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  actions?: ReactNode[];
  extra?: ReactNode;
}) {
  return (
    <div
      {...props}
      className={[styles.item, 'crm-list-item', className]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
      {extra != null && <div className={styles.extra}>{extra}</div>}
      {!!actions?.length && (
        <ul className={styles.actions}>
          {actions.map((action, index) => (
            <li
              key={
                React.isValidElement(action) && action.key != null
                  ? action.key
                  : index
              }
            >
              {action}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Meta({
  avatar,
  title,
  description,
  className,
  ...props
}: Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
  avatar?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
}) {
  return (
    <div
      {...props}
      className={[styles.meta, className].filter(Boolean).join(' ')}
    >
      {avatar != null && <div className={styles.avatar}>{avatar}</div>}
      <div className={styles.content}>
        {title != null && <div className={styles.title}>{title}</div>}
        {description != null && (
          <div className={styles.description}>{description}</div>
        )}
      </div>
    </div>
  );
}

export const CrmList = Object.assign(CrmListRoot, {
  Item: Object.assign(Item, { Meta }),
});
